import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {TableItemProps} from 'notebook/mediocre/MediocreConstants';
import {NoteLinkedGroup} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import {NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import {ColumnDefinition, getColumnLabel} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {BaseTypedValueType, Table, TableData, TypedValue} from 'notebook/tables/TableConstants';
import TypedValueEditor from 'notebook/tables/typed-values/components/editors/TypedValueEditor';
import {resolveTypedValueWithRedux} from 'notebook/tables/typed-values/TypedValueResolution';
import {NotePart} from 'notebook/util/NoteUtilConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';

import useHydratedStructuredTableData from './hooks/useHydratedStructuredTableData';
import {
  LastSyncedUpstreamValue,
  StructuredTable,
  UnknownStructuredTableAdapter,
} from './StructuredTableConstants';
import {NoteLinkedStructuredTable} from './StructuredTablePlugin';

interface StructuredTableFormProps<TTableMetadata, TRowMetadata> {
  notePart: NotePart & {item: NoteLinkedStructuredTable};
  index: number;
  itemProps: TableItemProps;
  editorProps: NoteEditorProps<NotePart | NoteLinkedGroup<unknown>>;
  adapter: UnknownStructuredTableAdapter;
  // Our updateHandler which gets threaded back to the StructuredTablePlugin.setContent.  It needs to know
  // how to handle updating the StructuredTable from a TableData or a StructuredTable format.  It also needs
  // to know how to update the LastSyncedUpstreamValues, if provided.
  updateHandler: (
    index: number,
    content: {
      item?: StructuredTable<TTableMetadata, TRowMetadata>;
      table?: TableData;
      lastSyncedUpstreamValuesByRow?: Array<Array<LastSyncedUpstreamValue>>;
    }
  ) => void;
  isInGroup?: boolean;
}

// We track the query strings used for each form field
interface Queries {
  [columnIndex: number]: string;
}

export default function StructuredTableForm<TTableMetadata, TRowMetadata>(
  props: StructuredTableFormProps<TTableMetadata, TRowMetadata>
): JSX.Element | null {
  const {notePart, adapter, index: notePartIndex} = props;
  const structuredTable = notePart.item as unknown as StructuredTable<TTableMetadata, TRowMetadata>;

  const {table, staleCellIndices} = useHydratedStructuredTableData({
    adapter,
    structuredTable,
    computedTable: props.itemProps.computedTable,
    validationErrors: props.itemProps.validationErrors,
    inputStoreData: adapter.getStoreDataForLiveValues(structuredTable),
  });
  assert(table.table.length === 1, 'StructuredTableForm can only be used on a table with exactly one row');

  function updateOneTypedValue(columnIndex: number, typedValue: TypedValue | null): void {
    props.editorProps.storeState();
    const sourceRow = structuredTable.rows[0];
    const row = {...sourceRow, cells: [...sourceRow.cells]};
    const sourceCell = row.cells[columnIndex];
    row.cells[columnIndex] = {
      ...sourceCell,
      typedValue,
      text: '',
    };
    props.updateHandler(notePartIndex, {
      item: {
        ...structuredTable,
        rows: [row],
      },
    });
  }

  return (
    <div
      className={classNames(
        'structuredTableForm',
        props.isInGroup && css(styles.formInsideGroup),
        props.isInGroup &&
          BenchlingConfig.TEMP_UPDATED_MIXTURE_PREP_EXPORT_STYLE &&
          'notebook-export--table-form-inside-group'
      )}
      {...getTestProps({component: 'StructuredTableForm', element: 'root'})}
    >
      <StructuredTableFormControls
        table={table}
        computedTable={props.itemProps.computedTable}
        staleCellIndices={staleCellIndices}
        updateOneTypedValue={updateOneTypedValue}
        locked={props.editorProps.locked}
        adapter={adapter}
        viewer={props.editorProps.viewer}
      />
    </div>
  );
}

interface StructuredTableFormControlsProps {
  table: Table;
  computedTable: Array<Array<EvaluatedCell>>;
  staleCellIndices: Array<{rowIndex: number; colIndex: number}>;
  updateOneTypedValue: (columnIndex: number, typedValue: TypedValue | null) => void;
  locked: boolean;
  adapter: UnknownStructuredTableAdapter;
  rowIndex?: number;
  viewer?: boolean;
}

function isCellStale(
  staleCellIndices: Array<{rowIndex: number; colIndex: number}>,
  cellRowIndex: number,
  cellColIndex: number
): boolean {
  return (
    null !=
    staleCellIndices.find(({rowIndex, colIndex}) => rowIndex === cellRowIndex && colIndex === cellColIndex)
  );
}

/**
 * Our default StructuredTableFormLayout which lays out the fields as 3 per row.
 */
export const FIELDS_PER_ROW = 3;

export function fieldsPerForRowStructuredTableFormLayout<TTableMetadata, TRowMetadata>(
  columns: Array<ColumnDefinition>,
  formFields: Array<JSX.Element>,
  fieldsPerRow: number = FIELDS_PER_ROW
): Array<JSX.Element> {
  return formFields.map((typedValueEditor, index) => {
    const wrapNow = index % fieldsPerRow === 0;
    return (
      <React.Fragment key={index}>
        {wrapNow && <Col className="clearfix" />}
        {typedValueEditor}
      </React.Fragment>
    );
  });
}

export function StructuredTableFormControls(props: StructuredTableFormControlsProps): JSX.Element | null {
  const {
    adapter,
    table,
    computedTable,
    updateOneTypedValue,
    staleCellIndices,
    locked: isEntireFormLocked,
    rowIndex,
    viewer,
  } = props;
  const [queries, setQueries] = useState<Queries>({});
  const tableRowIndex = rowIndex || 0;
  function updateOneQuery(columnIndex: number, query: string): void {
    setQueries({...queries, [columnIndex]: query});
  }

  const columns: Array<ColumnDefinition> = [];
  const typedValueEditors = table.columnConstraints.map((columnConstraint, columnIndex) => {
    const columnDefinition = columnConstraint!.columnDefinition!;
    columns.push(columnDefinition);

    // The logic here is pretty important. We prioritize the computed cell, with a few fallbacks to try to
    // get a usable typedValue.
    const evaluatedCell = computedTable[tableRowIndex][columnIndex];
    const rowMetadata = table.rowData?.[tableRowIndex].rowMetadata ?? null;
    const typedValue =
      // This is our preferred value, from the actual computed table
      evaluatedCell?.value ??
      // The live cell value, which is unlikely to exist if the computed table doesn't also exist
      table.table[tableRowIndex][columnIndex].typedValue ??
      // The column's default value is used if the cell has no value
      columnConstraint!.structuredTableColumn!.defaultValue?.typedValue ??
      // Try to create a blank typedValue of the correct type, if all else fails
      resolveTypedValueWithRedux(columnDefinition, {type: BaseTypedValueType.STRING, rawValue: ''})
        .resolvedValue;
    return adapter.renderFormField(
      <TypedValueEditor
        typedValue={typedValue}
        evaluatedCell={evaluatedCell}
        locked={isEntireFormLocked || columnDefinition.locked || evaluatedCell?.locked}
        stale={isCellStale(staleCellIndices, tableRowIndex, columnIndex)}
        query={queries[columnIndex]}
        onUpdateTypedValue={(tv) => {
          updateOneTypedValue(columnIndex, tv);
        }}
        onUpdateQuery={(query) => {
          updateOneQuery(columnIndex, query);
        }}
        columnConstraint={columnConstraint!}
        rowMetadata={rowMetadata}
        initialTypedValue={typedValue}
        label={getColumnLabel(columnDefinition)}
        viewer={viewer}
      />,
      columnDefinition,
      columnIndex
    );
  });
  return (
    <form
      className={classNames(
        css(styles.formGrow),
        BenchlingConfig.TEMP_UPDATED_MIXTURE_PREP_EXPORT_STYLE && 'notebook-export--table-form-grow'
      )}
      {...getTestProps({component: 'StructuredTableFormControls', element: 'root'})}
    >
      {props.adapter.renderFormContents(columns, typedValueEditors)}
    </form>
  );
}

const styles = StyleSheet.create({
  formGrow: {
    flex: 'auto',
  },
  formInsideGroup: {
    padding: '6px',
  },
});
